"use client";
import React, { useEffect, useRef, useState } from "react";
import {
  X, Package, Truck, MapPin, Calendar, Download, CheckCircle,
  Clock, User, Mail, Phone, ShoppingBag, RotateCcw, AlertCircle,
  Flag, Shield, AlertTriangle, ChevronRight, Info, RefreshCw,
  Printer, ImageOff, Zap, Search, Play, Image, FileText,
} from "lucide-react";
import { useOrders } from "../context/OrdersContext";

/* ── Helpers ─────────────────────────────────────────────── */

const isCustomOrder = (order) => {
  if (!order) return false;
  return (order.items || []).some((i) => i.customisations === "Yes");
};

const isPrepaidOrder = (order) => {
  const method = (order?.payment_method || order?.paymentMethod || "COD").toLowerCase();
  return !["cod", "cash on delivery", "cash"].includes(method);
};

const formatDate = (ds) =>
  ds
    ? new Date(ds).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
    : "—";

const AVATAR_COLORS = [
  "bg-teal-500", "bg-violet-500", "bg-pink-500", "bg-amber-500", "bg-blue-500",
  "bg-emerald-500", "bg-red-500", "bg-purple-500", "bg-sky-500", "bg-orange-500",
];

const avatarColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";

/* ── Status Config ───────────────────────────────────────── */

const STATUS_CONFIG = {
  Placed:                     { bg: "bg-teal-100",    text: "text-teal-700",    border: "border-teal-200",    dot: "bg-teal-500",    icon: ShoppingBag, timelineBg: "bg-teal-100",    timelineText: "text-teal-700"    },
  Processing:                 { bg: "bg-blue-100",    text: "text-blue-700",    border: "border-blue-200",    dot: "bg-blue-500",    icon: Clock,       timelineBg: "bg-blue-100",    timelineText: "text-blue-700"    },
  "Printing Started":         { bg: "bg-indigo-100",  text: "text-indigo-700",  border: "border-indigo-200",  dot: "bg-indigo-500",  icon: Printer,     timelineBg: "bg-indigo-100",  timelineText: "text-indigo-700"  },
  "Production Completed":     { bg: "bg-cyan-100",    text: "text-cyan-700",    border: "border-cyan-200",    dot: "bg-cyan-500",    icon: Zap,         timelineBg: "bg-cyan-100",    timelineText: "text-cyan-700"    },
  "Quality Check":            { bg: "bg-yellow-100",  text: "text-yellow-700",  border: "border-yellow-200",  dot: "bg-yellow-500",  icon: Shield,      timelineBg: "bg-yellow-100",  timelineText: "text-yellow-700"  },
  Packaging:                  { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-500",   icon: Package,     timelineBg: "bg-amber-100",   timelineText: "text-amber-700"   },
  Shipped:                    { bg: "bg-violet-100",  text: "text-violet-700",  border: "border-violet-200",  dot: "bg-violet-500",  icon: Truck,       timelineBg: "bg-violet-100",  timelineText: "text-violet-700"  },
  Delivered:                  { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle, timelineBg: "bg-emerald-100", timelineText: "text-emerald-700" },
  "Return Requested":         { bg: "bg-sky-100",     text: "text-sky-700",     border: "border-sky-200",     dot: "bg-sky-500",     icon: RotateCcw,   timelineBg: "bg-sky-100",     timelineText: "text-sky-700"     },
  "Return Approved":          { bg: "bg-teal-100",    text: "text-teal-700",    border: "border-teal-200",    dot: "bg-teal-400",    icon: RotateCcw,   timelineBg: "bg-teal-100",    timelineText: "text-teal-700"    },
  "Return Rejected":          { bg: "bg-red-100",     text: "text-red-700",     border: "border-red-200",     dot: "bg-red-500",     icon: AlertCircle, timelineBg: "bg-red-100",     timelineText: "text-red-700"     },
  Returned:                   { bg: "bg-gray-100",  text: "text-orange-700",  border: "border-gray-200",  dot: "bg-orange-500",  icon: RotateCcw,   timelineBg: "bg-gray-100",  timelineText: "text-orange-700"  },
  "Inspection Pending":       { bg: "bg-purple-100",  text: "text-purple-700",  border: "border-purple-200",  dot: "bg-purple-400",  icon: Shield,      timelineBg: "bg-purple-100",  timelineText: "text-purple-700"  },
  "Inspection Passed":        { bg: "bg-green-100",   text: "text-green-700",   border: "border-green-200",   dot: "bg-green-500",   icon: CheckCircle, timelineBg: "bg-green-100",   timelineText: "text-green-700"   },
  "Inspection Failed":        { bg: "bg-red-100",     text: "text-red-700",     border: "border-red-200",     dot: "bg-red-600",     icon: AlertCircle, timelineBg: "bg-red-100",     timelineText: "text-red-700"     },
  "Refund Processing":        { bg: "bg-lime-100",    text: "text-lime-700",    border: "border-lime-200",    dot: "bg-lime-500",    icon: RefreshCw,   timelineBg: "bg-lime-100",    timelineText: "text-lime-700"    },
  Refunded:                   { bg: "bg-green-100",   text: "text-green-700",   border: "border-green-200",   dot: "bg-green-500",   icon: CheckCircle, timelineBg: "bg-green-100",   timelineText: "text-green-700"   },
  "Partial Refund":           { bg: "bg-lime-100",    text: "text-lime-700",    border: "border-lime-200",    dot: "bg-lime-400",    icon: RefreshCw,   timelineBg: "bg-lime-100",    timelineText: "text-lime-700"    },
  "Replacement Processing":   { bg: "bg-sky-100",     text: "text-sky-700",     border: "border-sky-200",     dot: "bg-sky-400",     icon: RefreshCw,   timelineBg: "bg-sky-100",     timelineText: "text-sky-700"     },
  Cancelled:                  { bg: "bg-red-100",     text: "text-red-700",     border: "border-red-200",     dot: "bg-red-500",     icon: X,           timelineBg: "bg-red-100",     timelineText: "text-red-700"     },
  "Cancelled (Not Refunded)": { bg: "bg-red-100",     text: "text-red-700",     border: "border-red-200",     dot: "bg-red-600",     icon: X,           timelineBg: "bg-red-100",     timelineText: "text-red-700"     },
  "Cancellation Requested":   { bg: "bg-rose-100",    text: "text-rose-700",    border: "border-rose-200",    dot: "bg-rose-400",    icon: AlertCircle, timelineBg: "bg-rose-100",    timelineText: "text-rose-700"    },
  Rejected:                   { bg: "bg-rose-100",    text: "text-rose-700",    border: "border-rose-200",    dot: "bg-rose-600",    icon: AlertCircle, timelineBg: "bg-rose-100",    timelineText: "text-rose-700"    },
  "Returned (No Refund)":     { bg: "bg-red-100",     text: "text-red-700",     border: "border-red-200",     dot: "bg-red-700",     icon: X,           timelineBg: "bg-red-100",     timelineText: "text-red-700"     },
  Completed:                  { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-600", icon: CheckCircle, timelineBg: "bg-emerald-100", timelineText: "text-emerald-700" },
};

/* ── Status Normalisation ────────────────────────────────── */

const BACKEND_MAP = {
  placed:                       "Placed",
  Placed:                       "Placed",
  processing:                   "Processing",
  Processing:                   "Processing",
  processing_started:           "Processing",
  Paid:                         "Processing",
  paid:                         "Processing",
  printing_started:             "Printing Started",
  "Printing Started":           "Printing Started",
  production_completed:         "Production Completed",
  "Production Completed":       "Production Completed",
  quality_check:                "Quality Check",
  "Quality Check":              "Quality Check",
  packaging:                    "Packaging",
  Packaging:                    "Packaging",
  packed:                       "Packaging",
  shipped:                      "Shipped",
  Shipped:                      "Shipped",
  out_for_delivery:             "Shipped",
  delivered:                    "Delivered",
  Delivered:                    "Delivered",
  return_requested:             "Return Requested",
  "Return Requested":           "Return Requested",
  return_approved:              "Return Approved",
  "Return Approved":            "Return Approved",
  return_pickup_scheduled:      "Return Approved",
  return_in_transit:            "Return Approved",
  return_rejected:              "Return Rejected",
  "Return Rejected":            "Return Rejected",
  returned:                     "Returned",
  Returned:                     "Returned",
  return_received:              "Returned",
  "Received Product":           "Returned",
  received_product:             "Returned",
  inspection_pending:           "Inspection Pending",
  "Inspection Pending":         "Inspection Pending",
  inspection_passed:            "Inspection Passed",
  "Inspection Passed":          "Inspection Passed",
  return_verified:              "Inspection Passed",
  inspection_failed:            "Inspection Failed",
  "Inspection Failed":          "Inspection Failed",
  refund_processing:            "Refund Processing",
  "Refund Processing":          "Refund Processing",
  refund_initiated:             "Refund Processing",
  Refund:                       "Refund Processing",
  refunded:                     "Refunded",
  Refunded:                     "Refunded",
  refund_completed:             "Refunded",
  refund_complete:              "Refunded",
  partial_refund:               "Partial Refund",
  "Partial Refund":             "Partial Refund",
  returned_no_refund:           "Returned (No Refund)",
  "Returned (No Refund)":       "Returned (No Refund)",
  replacement_processing:       "Replacement Processing",
  "Replacement Processing":     "Replacement Processing",
  Replacement:                  "Replacement Processing",
  completed:                    "Completed",
  Completed:                    "Completed",
  cancellation_requested:       "Cancellation Requested",
  "Cancellation Requested":     "Cancellation Requested",
  cancel_requested:             "Cancellation Requested",
  "Cancelled-Refunded":         "Cancelled",
  "cancelled-refunded":         "Cancelled",
  cancelled:                    "Cancelled",
  Cancelled:                    "Cancelled",
  "Cancelled-NotRefunded":      "Cancelled (Not Refunded)",
  "cancelled-notrefunded":      "Cancelled (Not Refunded)",
  Rejected:                     "Rejected",
  rejected:                     "Rejected",
};

const normaliseStatus = (raw = "") => {
  if (!raw) return "Placed";
  if (STATUS_CONFIG[raw]) return raw;
  if (BACKEND_MAP[raw]) return BACKEND_MAP[raw];
  const lower = raw.toLowerCase();
  for (const [k, v] of Object.entries(BACKEND_MAP)) {
    if (k.toLowerCase() === lower) return v;
  }
  return "Placed";
};

const STATUS_TO_API = {
  "Placed":                   "placed",
  "Processing":               "processing",
  "Printing Started":         "printing_started",
  "Production Completed":     "production_completed",
  "Quality Check":            "quality_check",
  "Packaging":                "packaging",
  "Shipped":                  "shipped",
  "Delivered":                "delivered",
  "Return Requested":         "return_requested",
  "Return Approved":          "return_approved",
  "Return Rejected":          "return_rejected",
  "Returned":                 "return_received",
  "Inspection Pending":       "inspection_pending",
  "Inspection Passed":        "inspection_passed",
  "Inspection Failed":        "inspection_failed",
  "Refund Processing":        "refund_processing",
  "Refunded":                 "refunded",
  "Partial Refund":           "partial_refund",
  "Replacement Processing":   "replacement_processing",
  "Completed":                "completed",
  "Cancellation Requested":   "cancellation_requested",
  "Cancelled":                "cancelled-refunded",
  "Cancelled (Not Refunded)": "cancelled-notrefunded",
  "Rejected":                 "rejected",
  "Returned (No Refund)":     "returned_no_refund",
};

/* ── Policy & Reason Constants ───────────────────────────── */

const CANCEL_REASONS_STANDARD = ["Customer Requested", "No Stock", "Duplicate Order", "Payment Failed", "Other"];
const CANCEL_REASONS_CUSTOM   = ["Customer Requested (before printing)", "No Stock / Material Unavailable", "Duplicate Order", "Other"];

const canCancel = (status, isCustom) => {
  const always = ["Placed", "Processing"];
  return isCustom ? always.includes(status) : [...always, "Packaging"].includes(status);
};

/**
 * Return reasons — each entry carries the id the backend expects in
 * `return_reason` (sent as a string, e.g. "1") alongside the label shown
 * in the UI. IMPORTANT: keep these ids in sync with whatever your backend
 * / return-reasons table actually uses. If your reasons are served
 * dynamically from an API, replace this static list with a fetch and keep
 * the same { id, label } shape — everything downstream already expects it.
 */
const RETURN_REASONS_STANDARD = [
  { id: "1", label: "Damaged product" },
  { id: "2", label: "Wrong product delivered" },
  { id: "3", label: "Missing items" },
  { id: "4", label: "Not as described" },
  { id: "5", label: "Change of mind" },
  { id: "6", label: "Size / fit issue" },
];

const RETURN_REASONS_CUSTOM = [
  { id: "1", label: "Damaged product" },
  { id: "2", label: "Wrong product delivered" },
  { id: "7", label: "Wrong image printed" },
  { id: "8", label: "Printing defect" },
  { id: "9", label: "Broken item" },
  { id: "3", label: "Missing items" },
];

// Lookup helper: given a reason id (string or number), returns the label.
// Used to render a human-readable reason when only the id was persisted.
const ALL_RETURN_REASONS = [...RETURN_REASONS_STANDARD, ...RETURN_REASONS_CUSTOM]
  .reduce((acc, r) => (acc[r.id] ? acc : { ...acc, [r.id]: r.label }), {});

const resolveReturnReasonLabel = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const key = String(value);
  return ALL_RETURN_REASONS[key] || value; // fall back to raw value if it's already a label
};

const RETURN_POLICY_STANDARD = `Return Policy (Standard Products):
• Returns accepted within 7 days of delivery.
• Product must be unused, in original packaging with all tags intact.
• Allowed reasons: damaged, wrong product, missing items, not as described, change of mind, size/fit issue.
• We will inspect the returned product on receipt.
• Refund is processed only after inspection approval.
• Refunds are credited within 5–7 business days after inspection.`;

const RETURN_POLICY_CUSTOM = `Return Policy (Custom Print Products):
• Returns accepted ONLY for valid issues: damaged product, wrong product delivered, wrong image printed, printing defect, broken item, missing items.
• NOT accepted: change of mind, wrong image uploaded by customer, wrong size selected, color variation (screen vs print), personal dislike.
• Product must be returned unused and in original packaging.
• We will inspect on receipt and compare claim vs actual.
• Refund / replacement processed after inspection approval.
• Replacement is preferred over refund for custom products.`;

const INSPECTION_OPTIONS = [
  { value: "full_refund",      label: "Perfect Condition / Genuine Defect", desc: "Same product, unused, genuine issue → Full refund",         result: "Refunded",               icon: CheckCircle,   color: "green" },
  { value: "partial_refund",   label: "Minor / Packaging Damage",           desc: "Minor damage or packaging damage → Partial refund",         result: "Partial Refund",         icon: AlertTriangle, color: "amber" },
  { value: "replacement",      label: "Valid Issue – Offer Replacement",    desc: "Genuine defect on custom product → Reprocess & ship again", result: "Replacement Processing", icon: RefreshCw,     color: "sky"   },
  { value: "reject_no_refund", label: "Customer Misuse / Fake Return",      desc: "Wrong/damaged/used product returned → No refund + flag",    result: "Returned (No Refund)",   icon: AlertCircle,   color: "red"   },
];

/* ── Style Maps ──────────────────────────────────────────── */

const COLOR_MAP = {
  blue:    { solid: "bg-blue-600 hover:bg-blue-700 text-white border-blue-600",          outline: "border-blue-300 text-blue-700 hover:bg-blue-50"          },
  indigo:  { solid: "bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600",    outline: "border-indigo-300 text-indigo-700 hover:bg-indigo-50"    },
  amber:   { solid: "bg-amber-500 hover:bg-amber-600 text-white border-amber-500",       outline: "border-amber-300 text-amber-700 hover:bg-amber-50"       },
  violet:  { solid: "bg-violet-600 hover:bg-violet-700 text-white border-violet-600",    outline: "border-violet-300 text-violet-700 hover:bg-violet-50"    },
  emerald: { solid: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600", outline: "border-emerald-300 text-emerald-700 hover:bg-emerald-50" },
  red:     { solid: "bg-red-600 hover:bg-red-700 text-white border-red-600",             outline: "border-red-300 text-red-600 hover:bg-red-50"             },
  sky:     { solid: "bg-sky-600 hover:bg-sky-700 text-white border-sky-600",             outline: "border-sky-300 text-sky-700 hover:bg-sky-50"             },
  gray:    { solid: "bg-gray-600 hover:bg-gray-700 text-white border-gray-600",          outline: "border-gray-300 text-gray-600 hover:bg-gray-50"          },
  yellow:  { solid: "bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500",    outline: "border-yellow-300 text-yellow-700 hover:bg-yellow-50"    },
  purple:  { solid: "bg-purple-600 hover:bg-purple-700 text-white border-purple-600",    outline: "border-purple-300 text-purple-700 hover:bg-purple-50"    },
  orange:  { solid: "bg-orange-500 hover:bg-orange-600 text-white border-orange-500",    outline: "border-gray-300 text-orange-700 hover:bg-gray-50"    },
};

const INSPECTION_COLOR = {
  green: "bg-green-50 border-green-300 text-green-700",
  amber: "bg-amber-50 border-amber-300 text-amber-700",
  sky:   "bg-sky-50 border-sky-300 text-sky-700",
  red:   "bg-red-50 border-red-300 text-red-700",
};

const INSPECTION_RESULT_COLOR = {
  green: "bg-green-50 border-green-200 text-green-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  sky:   "bg-sky-50 border-sky-200 text-sky-700",
  red:   "bg-red-50 border-red-200 text-red-700",
};

const INFO_BOX_COLORS = {
  blue:   "bg-blue-50 border-blue-200 text-blue-700",
  sky:    "bg-sky-50 border-sky-200 text-sky-700",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  amber:  "bg-amber-50 border-amber-200 text-amber-700",
  orange: "bg-gray-50 border-gray-200 text-orange-700",
  purple: "bg-purple-50 border-purple-200 text-purple-700",
  green:  "bg-green-50 border-green-200 text-green-700",
  red:    "bg-red-50 border-red-200 text-red-700",
  gray:   "bg-gray-50 border-gray-200 text-gray-500",
};

/* ── Shared Components ───────────────────────────────────── */

const Avatar = ({ name = "", size = "md" }) => {
  const sz = { lg: "w-12 h-12 text-base", sm: "w-8 h-8 text-xs", md: "w-10 h-10 text-sm" }[size] || "w-10 h-10 text-sm";
  return (
    <div className={`${avatarColor(name)} ${sz} rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold`}>
      {getInitials(name)}
    </div>
  );
};

const StatusBadge = ({ status, size = "sm" }) => {
  const cfg  = STATUS_CONFIG[status] || STATUS_CONFIG.Placed;
  const Icon = cfg.icon;
  const sz   = size === "lg" ? "text-sm px-3 py-1.5 gap-1.5" : "text-xs px-2.5 py-1 gap-1";
  return (
    <span className={`inline-flex items-center font-semibold rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} ${sz}`}>
      <Icon className={size === "lg" ? "w-4 h-4" : "w-3 h-3"} />{status}
    </span>
  );
};

const ActionBtn = ({ color = "gray", icon, label, onClick, outline = false, disabled = false }) => {
  const cls = COLOR_MAP[color]?.[outline ? "outline" : "solid"] || COLOR_MAP.gray.solid;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
    >
      {icon}{label}
    </button>
  );
};

const CustomBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold border border-indigo-200">
    <Printer className="w-2.5 h-2.5" /> Customised
  </span>
);

const InfoBox = ({ color, icon: Icon, children }) => (
  <div className={`px-3 py-2.5 rounded-xl border text-xs flex items-start gap-1.5 ${INFO_BOX_COLORS[color] || INFO_BOX_COLORS.gray}`}>
    {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
    <span>{children}</span>
  </div>
);

/* ── Customisation Detail Block ──────────────────────────── */

const CustomisationBlock = ({ item }) => {
  if (item.customisations !== "Yes") return null;

  const hasImage = !!item.customisation_image;
  const hasText  = !!item.customisation_text;
  const hasData  =
    item.customisation_data &&
    typeof item.customisation_data === "object" &&
    Object.keys(item.customisation_data).length > 0;

  return (
    <div className="mt-2.5 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2.5">
      <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider flex items-center gap-1 mb-2">
        <Printer className="w-2.5 h-2.5" /> Customisation Details
      </p>

      {/* Customer uploaded image */}
      {hasImage && (
        <div className="flex items-center gap-2.5 mb-2">
          <img
            src={item.customisation_image}
            alt="Customer upload"
            className="w-12 h-12 rounded-md object-cover border border-indigo-200 flex-shrink-0"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div>
            <p className="text-[10px] font-semibold text-indigo-600">Customer uploaded image</p>
            <p className="text-[10px] text-indigo-400 mt-0.5 truncate max-w-[160px]">
              {item.customisation_image.split("/").pop()}
            </p>
          </div>
        </div>
      )}

      {/* Custom text */}
      {hasText && (
        <div className="flex items-start gap-1.5 mb-1.5">
          <span className="text-[10px] font-semibold text-indigo-500 flex-shrink-0 mt-0.5">Text:</span>
          <span className="text-[10px] text-indigo-700 break-words leading-relaxed">
            {item.customisation_text}
          </span>
        </div>
      )}

      {/* Any extra key-value pairs from customisation_data */}
      {hasData &&
        Object.entries(item.customisation_data)
          .filter(([, v]) => v)
          .map(([key, val]) => (
            <div key={key} className="flex items-start gap-1.5 mb-1">
              <span className="text-[10px] font-semibold text-indigo-500 capitalize flex-shrink-0 mt-0.5">
                {key.replace(/_/g, " ")}:
              </span>
              <span className="text-[10px] text-indigo-700 break-words">{String(val)}</span>
            </div>
          ))
      }

      {/* Fallback when no detail fields present */}
      {!hasImage && !hasText && !hasData && (
        <p className="text-[10px] text-indigo-500 italic">Customer customisation applied to this item.</p>
      )}
    </div>
  );
};

/* ── Sub-Modal Shell ─────────────────────────────────────── */

const SubModal = ({ children, wide = false }) => (
  <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
    <div className={`bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full ${wide ? "sm:max-w-2xl" : "sm:max-w-md"} p-4 sm:p-6 max-h-[90vh] overflow-y-auto`}>
      {children}
    </div>
  </div>
);

const SubModalHeader = ({ icon: Icon, iconBg, iconColor, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div>
      <h3 className="font-bold text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const SubModalFooter = ({ onBack, onConfirm, confirmLabel, confirmClass, disabled }) => (
  <div className="flex gap-3">
    <button
      onClick={onBack}
      className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
    >
      Go Back
    </button>
    <button
      onClick={onConfirm}
      disabled={disabled}
      className={`flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${confirmClass}`}
    >
      {confirmLabel}
    </button>
  </div>
);

/* ── Cancel Modal ────────────────────────────────────────── */

const CancelReasonModal = ({ isCustom, paymentMethod, onConfirm, onClose }) => {
  const [reason, setReason] = useState("");
  const [note,   setNote]   = useState("");

  const reasons   = isCustom ? CANCEL_REASONS_CUSTOM : CANCEL_REASONS_STANDARD;
  const isPrepaid = isPrepaidOrder({ payment_method: paymentMethod });
  const isRefund  = isPrepaid || ["No Stock", "No Stock / Material Unavailable"].includes(reason);

  return (
    <SubModal>
      <SubModalHeader
        icon={AlertTriangle}
        iconBg="bg-red-100"
        iconColor="text-red-600"
        title="Cancel Order"
        subtitle={isCustom ? "Custom orders can only be cancelled before printing starts" : "Select a reason for cancellation"}
      />

      <div className={`mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl text-xs border ${
        isPrepaid ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-600"
      }`}>
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          Payment: <strong>{paymentMethod || "COD"}</strong>
          {isPrepaid
            ? " — Prepaid order. Cancellation will automatically initiate a refund."
            : " — COD order. No payment to refund."
          }
        </span>
      </div>

      {isCustom && (
        <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs">
          <Printer className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>This is a <strong>customised order</strong>. Once printing starts, cancellation is strictly disabled.</span>
        </div>
      )}

      <div className="space-y-2 mb-4">
        {reasons.map((r) => (
          <button
            key={r}
            onClick={() => setReason(r)}
            className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
              reason === r ? "bg-red-50 border-red-300 text-red-700" : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
            }`}
          >
            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${reason === r ? "border-red-500" : "border-gray-300"}`}>
              {reason === r && <div className="w-2 h-2 rounded-full bg-red-500" />}
            </div>
            {r}
          </button>
        ))}
      </div>

      {reason === "Other" && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Please describe the reason…"
          rows={3}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 resize-none mb-4 focus:outline-none focus:ring-2 focus:ring-red-300"
        />
      )}

      {reason && (
        <div className={`mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl text-xs border ${
          isRefund ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"
        }`}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Order will be <strong>Cancelled</strong>.{" "}
            {isRefund
              ? <><strong>Refund will be processed</strong> to the original payment method within 5–7 business days.</>
              : "No refund will be issued (COD order)."
            }{" "}
            This action cannot be undone.
          </span>
        </div>
      )}

      <SubModalFooter
        onBack={onClose}
        onConfirm={() => {
          if (!reason) return;
          onConfirm(reason === "Other" ? (note.trim() || "Other") : reason, isRefund);
        }}
        confirmLabel="Confirm Cancel"
        confirmClass="bg-red-600 hover:bg-red-700"
        disabled={!reason || (reason === "Other" && !note.trim())}
      />
    </SubModal>
  );
};

/* ── Shipping Modal ──────────────────────────────────────── */

const ShippingDetailsModal = ({ onConfirm, onClose, initialData = {} }) => {
  const [vendor,    setVendor]    = useState(initialData.vendor         || "");
  const [tracking,  setTracking]  = useState(initialData.trackingNumber || "");
  const [pickup,    setPickup]    = useState(() => {
    if (!initialData.pickupTime) return "";
    try { return new Date(initialData.pickupTime).toISOString().slice(0, 16); } catch { return ""; }
  });
  const [submitted, setSubmitted] = useState(false);

  const isEdit = !!(initialData.vendor || initialData.trackingNumber);
  const valid  = vendor.trim() && tracking.trim() && pickup.trim();

  const fields = [
    { label: "Vendor / Carrier *", value: vendor,   setter: setVendor,   placeholder: "e.g. Delhivery, BlueDart, DTDC…", mono: false },
    { label: "Tracking ID *",      value: tracking, setter: setTracking, placeholder: "e.g. DL1234567890IN",              mono: true  },
  ];

  return (
    <SubModal>
      <SubModalHeader
        icon={Truck}
        iconBg="bg-violet-100"
        iconColor="text-violet-600"
        title={isEdit ? "Edit Shipping Details" : "Shipping Details"}
        subtitle={isEdit ? "Update carrier details for this shipment" : "Fill in carrier details before marking as Shipped"}
      />

      <div className="space-y-4 mb-5">
        {fields.map(({ label, value, setter, placeholder, mono }) => (
          <div key={label}>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
            <input
              value={value}
              onChange={(e) => setter(e.target.value)}
              placeholder={placeholder}
              className={`w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${mono ? "font-mono" : ""}`}
            />
          </div>
        ))}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Pickup Date &amp; Time *</label>
          <input
            type="datetime-local"
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
      </div>

      <SubModalFooter
        onBack={onClose}
        onConfirm={() => {
          if (!valid || submitted) return;
          setSubmitted(true);
          onConfirm({ vendor: vendor.trim(), trackingNumber: tracking.trim(), pickupTime: pickup });
        }}
        confirmLabel={submitted ? "Submitting…" : isEdit ? "Update Shipping" : "Mark as Shipped"}
        confirmClass="bg-violet-600 hover:bg-violet-700"
        disabled={!valid || submitted}
      />
    </SubModal>
  );
};

/* ── Delivery Flag Modal ─────────────────────────────────── */

const FLAG_STEPS = [
  { count: 1, action: "Internal note" },
  { count: 2, action: "Account flagged" },
  { count: 3, action: "Manual approval required" },
  { count: 4, action: "COD & returns restricted" },
];

const DeliveryFlagModal = ({ currentFlags = 0, onFlag, onClose }) => {
  const newFlags  = currentFlags + 1;
  const willBlock = newFlags >= 4;

  return (
    <SubModal>
      <SubModalHeader
        icon={Flag}
        iconBg={willBlock ? "bg-red-100" : "bg-amber-100"}
        iconColor={willBlock ? "text-red-600" : "text-amber-600"}
        title="Flag Customer"
        subtitle="Customer was unavailable / did not answer"
      />

      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600">Current flags:</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center ${i <= currentFlags ? "bg-amber-400" : "bg-gray-100"}`}>
                <Flag className={`w-3 h-3 ${i <= currentFlags ? "text-white" : "text-gray-300"}`} />
              </div>
            ))}
          </div>
          <span className="text-xs text-gray-500">{currentFlags}/4</span>
        </div>

        <div className="mb-3 bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
          {FLAG_STEPS.map(({ count, action }) => (
            <div key={count} className={`flex justify-between px-3 py-2 text-xs border-b last:border-b-0 ${
              newFlags === count ? "bg-amber-50 font-semibold text-amber-800" : "text-gray-500"
            }`}>
              <span>{count} flag{count > 1 ? "s" : ""}</span>
              <span>{action}</span>
            </div>
          ))}
        </div>

        <div className={`rounded-xl p-3 text-sm ${
          willBlock ? "bg-red-50 border border-red-200 text-red-700" : "bg-amber-50 border border-amber-200 text-amber-700"
        }`}>
          <p className="flex items-start gap-2">
            {willBlock
              ? <><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />This will be the <strong>4th flag</strong>. COD &amp; returns will be <strong>restricted</strong> and account blocked.</>
              : <><Info className="w-4 h-4 flex-shrink-0 mt-0.5" />Adding flag {newFlags}/4.</>
            }
          </p>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Order status will move to <span className="font-semibold text-orange-600">Returned</span> (undeliverable).
      </p>

      <SubModalFooter
        onBack={onClose}
        onConfirm={() => onFlag(newFlags)}
        confirmLabel={willBlock ? "Block Account & Flag" : "Add Flag & Return"}
        confirmClass={willBlock ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600"}
      />
    </SubModal>
  );
};

/* ── Return Policy Modal ─────────────────────────────────── */

const ReturnPolicyModal = ({ isCustom, onAccept, onClose }) => {
  const [accepted,        setAccepted]        = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState(null);
  const [note,            setNote]            = useState("");
  const [mediaDragOver,   setMediaDragOver]   = useState(false);
  const [images,          setImages]          = useState([]);
  const [videos,          setVideos]          = useState([]);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const reasons = isCustom ? RETURN_REASONS_CUSTOM  : RETURN_REASONS_STANDARD;
  const policy  = isCustom ? RETURN_POLICY_CUSTOM   : RETURN_POLICY_STANDARD;

  const toBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsDataURL(file);
  });

  const handleFiles = async (files) => {
    const imgs = [], vids = [];
    for (const file of Array.from(files)) {
      const base64 = await toBase64(file);
      const preview = URL.createObjectURL(file);
      if (file.type.startsWith("image/")) imgs.push({ preview, base64, name: file.name });
      else if (file.type.startsWith("video/")) vids.push({ preview, base64, name: file.name });
    }
    setImages((prev) => [...prev, ...imgs].slice(0, 5));
    setVideos((prev) => [...prev, ...vids].slice(0, 2));
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));
  const removeVideo = (idx) => setVideos((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = (e) => {
    e.preventDefault();
    setMediaDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const selectedReason = reasons.find((r) => r.id === selectedReasonId);

  return (
    <SubModal>
      <SubModalHeader
        icon={Shield}
        iconBg="bg-gray-100"
        iconColor="text-orange-600"
        title="Initiate Return"
        subtitle={isCustom ? "Custom products have strict return rules" : "Customer must agree before return is initiated"}
      />

      <div className="mb-3">
        <p className="text-xs font-semibold text-gray-600 mb-2">Return Reason *</p>
        <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
          {reasons.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedReasonId(r.id)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
                selectedReasonId === r.id
                  ? "bg-gray-50 border-gray-300 text-orange-700 font-semibold"
                  : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selectedReasonId === r.id ? "border-orange-500" : "border-gray-300"}`}>
                {selectedReasonId === r.id && <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />}
              </div>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-3 mb-3 max-h-32 overflow-y-auto border border-gray-100">
        <pre className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed font-sans">{policy}</pre>
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Additional notes (optional)…"
        rows={2}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-700 resize-none mb-3 focus:outline-none focus:ring-2 focus:ring-orange-300"
      />

      <label className="flex items-start gap-3 mb-4 cursor-pointer">
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-orange-500 flex-shrink-0" />
        <span className="text-sm text-gray-700">Customer has read and agreed to the return policy</span>
      </label>

      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          onClick={() => {
            if (!accepted || !selectedReasonId) return;
            onAccept(
              selectedReasonId,               // reason id → sent to backend as return_reason (e.g. "1")
              selectedReason?.label || "",    // reason label → used for UI/timeline display
              note,
              images.map((i) => i.preview),
              videos.map((v) => v.preview),
              images.map((i) => i.base64),
              videos.map((v) => v.base64),
            );
          }}
          disabled={!accepted || !selectedReasonId}
          className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Initiate Return
        </button>
      </div>
    </SubModal>
  );
};

/* ── Return Inspect Modal ────────────────────────────────── */

const ReturnInspectModal = ({ order, isCustom, onApprove, onReject, onClose }) => {
  const [step,          setStep]          = useState("review");
  const [rejectReason,  setRejectReason]  = useState("");
  const [lightboxSrc,   setLightboxSrc]   = useState(null);

  // Prefer the id-based reason (returnReasonId) and resolve it to a label.
  // Falls back to any pre-existing text field for orders created before this change.
  const rawReturnReason = order?.returnReasonId ?? order?.returnReason ?? order?.return_reason;
  const returnReason      = resolveReturnReasonLabel(rawReturnReason);
  const returnDescription = order?.returnDescription || order?.return_description
                          || order?.returnNote       || order?.return_note        || "";

  const evidenceImages = order?.returnImages    || order?.return_images   ||
                         order?.evidence_images || order?.evidenceImages  || [];
  const evidenceVideos = order?.returnVideos    || order?.return_videos   ||
                         order?.evidence_videos || order?.evidenceVideos  || [];

  const rejectionReasons = isCustom
    ? ["Change of mind (not valid for custom products)", "Wrong image uploaded by customer", "Wrong size selected by customer", "Color variation — screen vs print", "Personal dislike", "Other"]
    : ["Return window expired", "Product used / tags removed", "Not in original packaging", "Reason not valid under policy", "Other"];

  if (step === "reject") {
    return (
      <SubModal>
        <SubModalHeader
          icon={AlertCircle}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          title="Reject Return Request"
          subtitle="Select reason for rejecting the return"
        />
        <div className="space-y-2 mb-4">
          {rejectionReasons.map((r) => (
            <button
              key={r}
              onClick={() => setRejectReason(r)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all ${
                rejectReason === r ? "bg-red-50 border-red-300 text-red-700 font-medium" : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${rejectReason === r ? "border-red-500 bg-red-500" : "border-gray-300"}`} />
              {r}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={() => setStep("review")} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
            Go Back
          </button>
          <button
            onClick={() => rejectReason && onReject(rejectReason)}
            disabled={!rejectReason}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Reject Return
          </button>
        </div>
      </SubModal>
    );
  }

  return (
    <SubModal wide>
      {lightboxSrc && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxSrc(null)}>
          <button className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors" onClick={() => setLightboxSrc(null)}>
            <X className="w-8 h-8" />
          </button>
          <img src={lightboxSrc} alt="Evidence" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}

      <SubModalHeader
        icon={Search}
        iconBg="bg-sky-100"
        iconColor="text-sky-600"
        title="Inspect Return Request"
        subtitle="Review customer-submitted evidence before approving or rejecting"
      />

      <div className="mb-4 space-y-3">
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <p className="text-[10px] font-bold text-orange-500 uppercase tracking-wider mb-1">Return Reason</p>
          <p className="text-sm font-semibold text-gray-800">{returnReason}</p>
        </div>
        {returnDescription && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <FileText className="w-3 h-3" />Customer Description
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">{returnDescription}</p>
          </div>
        )}
      </div>

      {evidenceImages.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Image className="w-3.5 h-3.5" />Evidence Images ({evidenceImages.length})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {evidenceImages.map((src, idx) => (
              <button
                key={idx}
                onClick={() => setLightboxSrc(src)}
                className="group relative aspect-square rounded-xl overflow-hidden border border-gray-200 hover:border-sky-400 transition-all"
              >
                <img
                  src={src}
                  alt={`Evidence ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                />
                <div className="hidden w-full h-full items-center justify-center bg-gray-100 text-gray-400">
                  <ImageOff className="w-6 h-6" />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <Search className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {evidenceVideos.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Play className="w-3.5 h-3.5" />Evidence Videos ({evidenceVideos.length})
          </p>
          <div className="space-y-2">
            {evidenceVideos.map((src, idx) => (
              <div key={idx} className="rounded-xl overflow-hidden border border-gray-200 bg-black">
                <video src={src} controls className="w-full max-h-48 object-contain" preload="metadata">
                  Your browser does not support the video tag.
                </video>
              </div>
            ))}
          </div>
        </div>
      )}

      {evidenceImages.length === 0 && evidenceVideos.length === 0 && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 border border-dashed border-gray-300 text-gray-400">
          <ImageOff className="w-5 h-5 flex-shrink-0" />
          <p className="text-xs">No images or videos were uploaded by the customer for this return request.</p>
        </div>
      )}

      {order?.items?.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Ordered Items (for reference)</p>
          <div className="space-y-2">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl">
                {item.image
                  ? <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-gray-200" />
                  : <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0"><Package className="w-4 h-4 text-gray-400" /></div>
                }
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-800 truncate">{item.name}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Qty: {item.quantity} · ₹{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
        <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          onClick={() => setStep("reject")}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-red-300 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-50 transition-colors"
        >
          <AlertCircle className="w-4 h-4" />Reject Return
        </button>
        <button
          onClick={onApprove}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-sm font-semibold transition-colors"
        >
          <CheckCircle className="w-4 h-4" />Approve Return
        </button>
      </div>
    </SubModal>
  );
};

/* ── Return Inspection Modal ─────────────────────────────── */

const ReturnInspectionModal = ({ isCustom, onDecision, onClose }) => {
  const [decision, setDecision] = useState("");

  const options  = isCustom ? INSPECTION_OPTIONS : INSPECTION_OPTIONS.filter((o) => o.value !== "replacement");
  const selected = INSPECTION_OPTIONS.find((o) => o.value === decision);

  const confirmLabel = {
    full_refund:      "Process Refund",
    partial_refund:   "Process Partial Refund",
    replacement:      "Send Replacement",
    reject_no_refund: "Mark No Refund",
  }[decision] || "Confirm";

  const confirmClass = {
    full_refund:      "bg-green-600 hover:bg-green-700",
    partial_refund:   "bg-amber-500 hover:bg-amber-600",
    replacement:      "bg-sky-600 hover:bg-sky-700",
    reject_no_refund: "bg-red-600 hover:bg-red-700",
  }[decision] || "bg-gray-600 hover:bg-gray-700";

  return (
    <SubModal>
      <SubModalHeader
        icon={Package}
        iconBg="bg-blue-100"
        iconColor="text-blue-600"
        title="Inspection"
        subtitle={isCustom ? "Custom product — replacement preferred over refund" : "Is the returned product in acceptable condition?"}
      />

      <div className="space-y-2 mb-4">
        {options.map(({ value, icon: Icon, label, desc, color }) => (
          <button
            key={value}
            onClick={() => setDecision(value)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
              decision === value ? INSPECTION_COLOR[color] : "bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300"
            }`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <div className="text-left">
              <div className="font-semibold text-sm">{label}</div>
              <div className="text-xs opacity-75 mt-0.5">{desc}</div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className={`mb-4 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium border ${INSPECTION_RESULT_COLOR[selected.color]}`}>
          Order will be moved to <strong className="ml-1">{selected.result}</strong>.
          {decision === "reject_no_refund" && " Customer account will be flagged."}
        </div>
      )}

      <SubModalFooter
        onBack={onClose}
        onConfirm={() => decision && onDecision(decision, selected)}
        confirmLabel={confirmLabel}
        confirmClass={confirmClass}
        disabled={!decision}
      />
    </SubModal>
  );
};

/* ── Main Modal ──────────────────────────────────────────── */

const OrderDetailsModal = ({ isOpen, onClose, order, onDownloadInvoice, onStatusChange }) => {
  const modalRef = useRef(null);
  const { updateOrderStatus, fetchOrders, uploadReturnMedia } = useOrders();

  const [activeSubModal, setActiveSubModal] = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [saveSuccess,    setSaveSuccess]    = useState(false);
  const [localStatus,    setLocalStatus]    = useState(null);
  const [localOrderData, setLocalOrderData] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setActiveSubModal(null);
      setSaveSuccess(false);
      setLocalStatus(null);
      setLocalOrderData({});
    }
  }, [isOpen]);

  if (!isOpen || !order) return null;

  const mergedOrder   = { ...order, ...localOrderData };
  const currentStatus = localStatus ?? normaliseStatus(order.rawStatus || order.status);
  const currentCfg    = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.Placed;
  const isCustom      = isCustomOrder(order);
  const isPrepaid     = isPrepaidOrder(order);
  const canDownload   = ["Delivered", "Refunded", "Partial Refund", "Completed"].includes(currentStatus);
  const currentFlags  = order.customerFlags ?? 0;

  const subtotal = order.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0;
  const shipping  = order.shipping ?? 0;
  const tax       = order.tax      ?? 0;
  const total     = order.total    ?? subtotal + shipping + tax;

  const commitStatus = async (newDisplayStatus, extra = {}) => {
    setSaving(true);
    const apiSlug = STATUS_TO_API[newDisplayStatus] || newDisplayStatus.toLowerCase().replace(/ /g, "_");
    const success = await updateOrderStatus(order.id, apiSlug, extra);
    setSaving(false);

    if (success) {
      // Was this order ALREADY "Shipped" before this action? If so, the
      // ShippingDetailsModal was opened via "Edit Shipping Details" and we're
      // only patching carrier info on the existing Shipped entry — no new
      // timeline row. If the order was NOT already Shipped (e.g. coming from
      // Packaging or Replacement Processing), this is a genuine status
      // transition and needs its own timeline entry, even though the payload
      // also happens to carry vendor/trackingNumber/pickupTime.
      const isShippingEdit = extra.isShippingEdit === true;

      setLocalStatus(newDisplayStatus);
      setLocalOrderData((prev) => ({
        ...prev,
        ...(extra.vendor         !== undefined && { vendor:         extra.vendor         }),
        ...(extra.trackingNumber !== undefined && { trackingNumber: extra.trackingNumber }),
        ...(extra.pickupTime     !== undefined && { pickupTime:     extra.pickupTime     }),
        ...(extra.returnReason   !== undefined && { returnReason:   extra.returnReason   }),
        ...(extra.returnReasonId !== undefined && { returnReasonId: extra.returnReasonId }),
        ...(extra.returnNote     !== undefined && { returnNote:     extra.returnNote     }),
        ...(extra.returnImages   !== undefined && { returnImages:   extra.returnImages   }),
        ...(extra.returnVideos   !== undefined && { returnVideos:   extra.returnVideos   }),
        ...(extra.rejectionReason !== undefined && { rejectionReason: extra.rejectionReason }),
        ...(!isShippingEdit && {
          timeline: [
            ...(prev.timeline || order.timeline || []),
            {
              id:              Date.now(),
              status:          newDisplayStatus,
              date:            new Date().toISOString(),
              time:            new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }),
              description:     extra.description || (
                newDisplayStatus === "Cancelled" ? "Order cancelled" :
                newDisplayStatus === "Shipped"   ? "Order shipped"   :
                newDisplayStatus === "Delivered" ? "Order delivered successfully" :
                newDisplayStatus
              ),
              cancelReason:    extra.cancelReason    || null,
              returnReason:    extra.returnReason    || null,
              rejectionReason: extra.rejectionReason || null,
              flagCount:       extra.flag_count      || null,
            },
          ],
        }),
      }));

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
      onStatusChange?.(newDisplayStatus, extra);
      await fetchOrders();
    }

    setActiveSubModal(null);
  };

  const renderActions = () => {
    switch (currentStatus) {
      case "Placed":
        return (
          <div className="flex flex-col gap-2">
            <ActionBtn color="blue" icon={<ChevronRight className="w-4 h-4" />} label="Move to Processing" onClick={() => commitStatus("Processing")} />
            {canCancel(currentStatus, isCustom) && (
              <ActionBtn color="red" icon={<X className="w-4 h-4" />} label="Cancel Order" onClick={() => setActiveSubModal("cancel")} outline />
            )}
          </div>
        );

      case "Processing":
        return (
          <div className="flex flex-col gap-2">
            {isCustom
              ? <ActionBtn color="indigo" icon={<Printer className="w-4 h-4" />} label="Start Printing"        onClick={() => commitStatus("Printing Started")} />
              : <ActionBtn color="yellow" icon={<Shield  className="w-4 h-4" />} label="Move to Quality Check" onClick={() => commitStatus("Quality Check")}    />
            }
            {canCancel(currentStatus, isCustom) && (
              <ActionBtn color="red" icon={<X className="w-4 h-4" />} label="Cancel Order" onClick={() => setActiveSubModal("cancel")} outline />
            )}
          </div>
        );

      case "Printing Started":
        return (
          <div className="flex flex-col gap-2">
            <InfoBox color="indigo" icon={AlertTriangle}>
              Cancellation is <strong>not allowed</strong> once printing has started.
            </InfoBox>
            <ActionBtn color="blue" icon={<Zap className="w-4 h-4" />} label="Mark Production Completed" onClick={() => commitStatus("Production Completed")} />
          </div>
        );

      case "Production Completed":
        return <ActionBtn color="yellow" icon={<Shield className="w-4 h-4" />} label="Move to Quality Check" onClick={() => commitStatus("Quality Check")} />;

      case "Quality Check":
        return <ActionBtn color="amber" icon={<Package className="w-4 h-4" />} label="Move to Packaging" onClick={() => commitStatus("Packaging")} />;

      case "Packaging":
        return <ActionBtn color="violet" icon={<Truck className="w-4 h-4" />} label="Mark as Shipped" onClick={() => setActiveSubModal("shipping")} />;

      case "Shipped":
        return (
          <div className="flex flex-col gap-2">
            <ActionBtn color="violet"  icon={<Truck       className="w-4 h-4" />} label="Edit Shipping Details"      onClick={() => setActiveSubModal("shipping")} />
            <ActionBtn color="emerald" icon={<CheckCircle className="w-4 h-4" />} label="Mark as Delivered"           onClick={() => commitStatus("Delivered")}     />
            <ActionBtn color="amber"   icon={<Flag        className="w-4 h-4" />} label="Customer Unavailable (Flag)" onClick={() => setActiveSubModal("flag")} outline />
          </div>
        );

      case "Delivered":
        return (
          <ActionBtn color="amber" icon={<RotateCcw className="w-4 h-4" />} label="Initiate Return" onClick={() => setActiveSubModal("returnPolicy")} outline />
        );

      case "Return Requested":
        return (
          <div className="flex flex-col gap-2">
            <InfoBox color="sky" icon={Info}>
              Customer has submitted a return request with evidence. Review before approving or rejecting.
            </InfoBox>
            <ActionBtn color="sky" icon={<Search className="w-4 h-4" />} label="Inspect Return Request" onClick={() => setActiveSubModal("returnInspect")} />
          </div>
        );

      case "Return Approved":
        return (
          <div className="flex flex-col gap-2">
            <InfoBox color="sky" icon={Info}>Waiting for customer to ship the product back. Once received, mark it below.</InfoBox>
            <ActionBtn
              color="orange"
              icon={<Package className="w-4 h-4" />}
              label="Mark as Received Product"
              onClick={() => commitStatus("Returned", { description: "Product received back from customer" })}
            />
          </div>
        );

      case "Cancellation Requested":
        return (
          <div className="flex flex-col gap-2">
            <InfoBox color="rose" icon={AlertTriangle}>
              Customer has requested cancellation. Review payment and reason before confirming.
            </InfoBox>
            <div className={`px-3 py-2.5 rounded-xl text-xs border flex items-start gap-1.5 ${
              isPrepaid ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-600"
            }`}>
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Payment: <strong>{order.payment_method || order.paymentMethod || "COD"}</strong>
                {isPrepaid ? " — Prepaid. Confirming will initiate a refund." : " — COD. No refund needed."}
              </span>
            </div>
            <ActionBtn
              color="red"
              icon={<X className="w-4 h-4" />}
              label={isPrepaid ? "Confirm Cancel & Refund" : "Confirm Cancel (No Refund)"}
              onClick={() =>
                commitStatus(
                  isPrepaid ? "Cancelled" : "Cancelled (Not Refunded)",
                  {
                    cancelReason: "Customer requested cancellation",
                    refundStatus: isPrepaid ? "Refund Processing" : "No Refund",
                    ...(isPrepaid && { initiateRefund: true }),
                  }
                )
              }
            />
            <ActionBtn
              color="blue"
              icon={<ChevronRight className="w-4 h-4" />}
              label="Reject Cancellation — Resume Order"
              onClick={() => commitStatus("Processing", { description: "Cancellation request rejected — order resumed" })}
              outline
            />
          </div>
        );

      case "Return Rejected":
        return <InfoBox color="red" icon={AlertCircle}>Return was <span className="font-semibold">Rejected</span> — no further changes allowed.</InfoBox>;

      case "Returned":
        return (
          <div className="flex flex-col gap-2">
            <InfoBox color="orange" icon={Info}>Product received back. Move to inspection queue to evaluate condition.</InfoBox>
            <ActionBtn color="purple" icon={<Shield className="w-4 h-4" />} label="Move to Inspection" onClick={() => commitStatus("Inspection Pending")} />
          </div>
        );

      case "Returned (No Refund)":
        return <InfoBox color="red" icon={AlertCircle}>Return received — no refund issued. Customer account has been flagged.</InfoBox>;

      case "Inspection Pending":
        return (
          <div className="flex flex-col gap-2">
            <InfoBox color="purple" icon={Info}>Product is in inspection queue. Record the inspection result below.</InfoBox>
            <ActionBtn color="purple" icon={<Package className="w-4 h-4" />} label="Inspect & Decide" onClick={() => setActiveSubModal("inspection")} />
          </div>
        );

      case "Inspection Passed":
        return (
          <div className="flex flex-col gap-2">
            <ActionBtn color="emerald" icon={<CheckCircle className="w-4 h-4" />} label="Process Full Refund" onClick={() => commitStatus("Refunded", { refundStatus: "Refund Processed" })} />
            {isCustom && (
              <ActionBtn color="sky" icon={<RefreshCw className="w-4 h-4" />} label="Send Replacement Order" onClick={() => commitStatus("Replacement Processing")} outline />
            )}
          </div>
        );

      case "Inspection Failed":
        return (
          <ActionBtn color="red" icon={<X className="w-4 h-4" />} label="Mark No Refund" onClick={() => commitStatus("Cancelled (Not Refunded)", { refundStatus: "No Refund" })} />
        );

      case "Refund Processing":
        return (
          <ActionBtn color="emerald" icon={<CheckCircle className="w-4 h-4" />} label="Mark Refund Completed" onClick={() => commitStatus("Refunded", { refundStatus: "Refund Completed" })} />
        );

      case "Replacement Processing":
        return (
          <ActionBtn color="violet" icon={<Truck className="w-4 h-4" />} label="Mark Replacement Shipped" onClick={() => setActiveSubModal("shipping")} />
        );

      case "Refunded":
      case "Partial Refund":
        return (
          <InfoBox color="green" icon={CheckCircle}>
            {currentStatus === "Partial Refund" ? "Partial refund has been processed" : "Refund has been processed"} — no further actions.
          </InfoBox>
        );

      case "Completed":
        return <InfoBox color="green" icon={CheckCircle}>Order completed — no further actions.</InfoBox>;

      default: {
        const isCancelled   = currentStatus === "Cancelled" || currentStatus === "Cancelled (Not Refunded)";
        const isNotRefunded = currentStatus === "Cancelled (Not Refunded)";
        const showRefundMsg = isCancelled && isPrepaid && !isNotRefunded;
        const showNoRefund  = isCancelled && (!isPrepaid || isNotRefunded);
        return (
          <div className="space-y-2">
            <InfoBox color="gray">
              Order is <span className="font-semibold text-gray-700">{currentStatus}</span> — no further changes allowed.
            </InfoBox>
            {showRefundMsg && (
              <InfoBox color="blue" icon={RefreshCw}>
                <strong>Refund will be processed</strong> to the original payment method within 5–7 business days.
              </InfoBox>
            )}
            {showNoRefund && (
              <InfoBox color="gray" icon={Info}>
                {isNotRefunded ? "No refund issued for this cancellation." : "COD order — no payment to refund."}
              </InfoBox>
            )}
          </div>
        );
      }
    }
  };

  const refundSummaryLabel = (() => {
    if (order.refundStatus)                         return order.refundStatus;
    if (currentStatus === "Refunded")               return "Refund Processed";
    if (currentStatus === "Refund Processing")      return "Refund Processing";
    if (currentStatus === "Partial Refund")         return "Partial Refund";
    if (currentStatus === "Cancelled" && isPrepaid) return "Refund Processing";
    return null;
  })();

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} aria-hidden="true" />

      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div ref={modalRef} className="w-full max-w-5xl max-h-[92vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">

          {/* ── Header ── */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-7 py-3 sm:py-5 border-b border-gray-100 bg-white border-b border-gray-100">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 id="modal-title" className="text-base sm:text-xl font-bold text-gray-900 leading-tight">Order Details</h2>
                  {isCustom && <CustomBadge />}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {order.orderNumber} &nbsp;·&nbsp; {formatDate(order.date)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusBadge status={currentStatus} size="lg" />
              {currentFlags > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-200">
                  <Flag className="w-3 h-3" />{currentFlags}
                </span>
              )}
              <button onClick={onClose} aria-label="Close modal" className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 p-4 sm:p-7">

              {/* ── Left column ── */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-5">

                {isCustom && (
                  <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-indigo-50 border border-indigo-200">
                    <Printer className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-indigo-800">Customised Order — Strict Policy Applies</p>
                      <p className="text-xs text-indigo-600 mt-0.5">
                        Cancellation allowed only before printing starts. Returns limited to valid defect/damage claims only. Replacement preferred over refund.
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Order Items ── */}
                <section className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Package className="w-4 h-4 text-teal-600" />Order Items
                    <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">{order.items?.length || 0}</span>
                  </h3>
                  <div className="space-y-3">
                    {order.items?.map((item, idx) => (
                      <div key={item.id || idx} className="flex gap-3 p-3 sm:p-3.5 bg-white rounded-xl border border-gray-100 hover:border-teal-200 hover:shadow-sm transition-all duration-200">

                        {/* Product image */}
                        <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {item.image
                            ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                            : <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-gray-300" /></div>
                          }
                        </div>

                        {/* Product info */}
                        <div className="flex-1 min-w-0">
                          {/* Name + customised badge */}
                          <div className="flex items-start justify-between gap-1">
                            <h4 className="font-semibold text-gray-900 text-sm leading-snug truncate">{item.name}</h4>
                            {item.customisations === "Yes" && (
                              <span className="flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200 flex items-center gap-0.5">
                                <Printer className="w-2 h-2" />Customised
                              </span>
                            )}
                          </div>

                          {item.category && <p className="text-xs text-gray-400 mt-0.5">{item.category}</p>}
                          {item.variant  && <p className="text-xs text-gray-400">{item.variant}</p>}

                          {/* ── Customisation Details Block ── */}
                          <CustomisationBlock item={item} />

                          {/* Qty + Price */}
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Qty: {item.quantity}</span>
                            <div className="text-right">
                              <span className="text-xs text-gray-400 block">₹{item.price?.toFixed(2)} each</span>
                              <span className="font-bold text-gray-900 text-sm">₹{(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ── Timeline ── */}
                {mergedOrder.timeline?.length > 0 && (
                  <section className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-5 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-teal-600" />Order Timeline
                    </h3>
                    <div className="space-y-0">
                      {mergedOrder.timeline.map((event, index) => {
                        const cfg    = STATUS_CONFIG[normaliseStatus(event.status)] || STATUS_CONFIG.Placed;
                        const IconEl = cfg.icon;
                        const isLast = index === mergedOrder.timeline.length - 1;
                        return (
                          <div key={index} className="flex gap-4">
                            <div className="flex flex-col items-center flex-shrink-0">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${cfg.timelineBg} ${cfg.timelineText}`}>
                                <IconEl className="w-4 h-4" />
                              </div>
                              {!isLast && <div className="w-0.5 h-10 bg-gradient-to-b from-gray-300 to-gray-100 my-1" />}
                            </div>
                            <div className={`flex-1 ${!isLast ? "pb-6" : ""}`}>
                              <div className="flex flex-wrap justify-between items-start gap-1">
                                <div>
                                  <h4 className="font-semibold text-gray-800 text-sm capitalize leading-tight">{normaliseStatus(event.status)}</h4>
                                  {event.description     && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{event.description}</p>}
                                  {event.cancelReason    && <p className="text-xs text-red-600 mt-0.5 font-medium">Reason: {event.cancelReason}</p>}
                                  {event.returnReason    && <p className="text-xs text-orange-600 mt-0.5 font-medium">Return reason: {resolveReturnReasonLabel(event.returnReason)}</p>}
                                  {event.rejectionReason && <p className="text-xs text-red-600 mt-0.5 font-medium">Rejected: {event.rejectionReason}</p>}
                                  {event.flagCount && (
                                    <p className="text-xs text-amber-600 mt-0.5 font-medium flex items-center gap-1">
                                      <Flag className="w-3 h-3" /> Flag {event.flagCount}/4 added
                                    </p>
                                  )}
                                </div>
                                <div className="text-right text-xs text-gray-400 flex-shrink-0">
                                  <div className="font-medium text-gray-500">{formatDate(event.date)}</div>
                                  <div>
                                    {event.time || (event.date
                                      ? new Date(event.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
                                      : ""
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>

              {/* ── Right column ── */}
              <div className="space-y-4 sm:space-y-5">

                {/* Order Actions */}
                <section className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${currentCfg.dot}`} />Order Actions
                  </h3>
                  {saving ? (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <span className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-gray-500">Saving…</span>
                    </div>
                  ) : saveSuccess ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-700">Status updated!</span>
                    </div>
                  ) : renderActions()}
                </section>

                {/* Customer Flags */}
                {currentFlags > 0 && (
                  <section className={`rounded-2xl p-4 border ${currentFlags >= 4 ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Flag className={`w-4 h-4 ${currentFlags >= 4 ? "text-red-600" : "text-amber-600"}`} />
                      <h3 className={`text-sm font-bold ${currentFlags >= 4 ? "text-red-700" : "text-amber-700"}`}>
                        {currentFlags >= 4 ? "Account Blocked" : "Customer Flags"}
                      </h3>
                    </div>
                    <div className="flex gap-1 mb-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center ${i <= currentFlags ? (currentFlags >= 4 ? "bg-red-500" : "bg-amber-400") : "bg-gray-200"}`}>
                          <Flag className={`w-3 h-3 ${i <= currentFlags ? "text-white" : "text-gray-400"}`} />
                        </div>
                      ))}
                    </div>
                    <p className={`text-xs ${currentFlags >= 4 ? "text-red-600" : "text-amber-600"}`}>
                      {currentFlags === 1 && "Internal note added."}
                      {currentFlags === 2 && "Account flagged."}
                      {currentFlags === 3 && "Manual approval required for future orders."}
                      {currentFlags >= 4  && "COD & returns restricted. Account blocked."}
                    </p>
                  </section>
                )}

                {/* Customer */}
                <section className="rounded-2xl p-4 sm:p-5 border border-teal-100 bg-teal-50/40">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-teal-600" />Customer
                  </h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="relative">
                      <Avatar name={order.customer?.name || "Unknown"} size="lg" />
                      {currentFlags > 0 && (
                        <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-white ${currentFlags >= 4 ? "bg-red-500" : "bg-amber-400"}`}>
                          {currentFlags}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-gray-900 text-sm leading-tight truncate">{order.customer?.name || "Unknown Customer"}</h4>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { icon: Mail,  value: order.customer?.email || "No email provided" },
                      { icon: Phone, value: order.customer?.phone || "No phone provided" },
                    ].map(({ icon: Icon, value }) => (
                      <div key={value} className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-3.5 h-3.5 text-teal-600" />
                        </div>
                        <span className="text-xs text-gray-600 truncate">{value}</span>
                      </div>
                    ))}
                  </div>
                  {(order.customer?.totalOrders || order.customer?.totalSpent) && (
                    <div className="mt-4 pt-4 border-t border-teal-100 grid grid-cols-2 gap-3">
                      <div className="text-center bg-white rounded-xl py-3 border border-teal-100">
                        <div className="text-lg font-bold text-teal-600">{order.customer.totalOrders}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Total Orders</div>
                      </div>
                      <div className="text-center bg-white rounded-xl py-3 border border-gray-100">
                        <div className="text-lg font-bold text-orange-500">₹{order.customer.totalSpent || 0}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Total Spent</div>
                      </div>
                    </div>
                  )}
                </section>

                {/* Order Summary */}
                <section className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Order Summary</h3>
                  <div className="space-y-2.5 text-sm">
                    {[["Subtotal", subtotal], ["Shipping", shipping], ["Tax", tax]].map(([label, val]) => (
                      <div key={label} className="flex justify-between items-center text-gray-600">
                        <span>{label}</span>
                        <span className="font-medium text-gray-800">₹{Number(val).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between items-center">
                      <span className="font-bold text-gray-800">Total</span>
                      <span className="text-lg font-bold text-teal-700">₹{Number(total).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between px-3 py-2.5 rounded-xl bg-white border border-gray-100">
                    <span className="text-xs text-gray-500">Payment mode</span>
                    <span className="text-xs font-semibold text-gray-700">{order.payment_method || order.paymentMethod || "COD"}</span>
                  </div>

                  {isCustom && currentFlags >= 4 && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200">
                      <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                      <span className="text-xs text-red-600 font-medium">COD disabled — account restricted</span>
                    </div>
                  )}

                  {refundSummaryLabel && (
                    <div className="mt-3 flex items-center justify-between px-3 py-2.5 rounded-xl bg-green-50 border border-green-200">
                      <span className="text-xs text-green-700 font-medium flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5" />Refund Status
                      </span>
                      <span className="text-xs font-semibold text-green-700">{refundSummaryLabel}</span>
                    </div>
                  )}

                  {currentStatus === "Replacement Processing" && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-sky-50 border border-sky-200">
                      <RefreshCw className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                      <span className="text-xs text-sky-700 font-medium">Replacement order in production</span>
                    </div>
                  )}
                </section>

                {/* Shipping Address */}
                {order.shippingAddress && (
                  <section className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-teal-600" />Shipping Address
                    </h3>
                    <div className="text-sm text-gray-600 space-y-1 leading-relaxed break-all">
                      <p className="font-semibold text-gray-800 break-words">{order.shippingAddress.name || order.customer?.name}</p>
                      {order.shippingAddress.street && <p>{order.shippingAddress.street}</p>}
                      <p>{[order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.zipCode].filter(Boolean).join(", ")}</p>
                      {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                    </div>
                  </section>
                )}

                {/* Billing Address */}
                {order.billingAddress && (
                  <section className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-500" />Billing Address
                    </h3>
                    {order.shippingAddress &&
                     order.billingAddress.street  === order.shippingAddress.street &&
                     order.billingAddress.zipCode === order.shippingAddress.zipCode ? (
                      <p className="text-xs text-gray-500 italic">Same as shipping address</p>
                    ) : (
                      <div className="text-sm text-gray-600 space-y-1 leading-relaxed">
                        {order.billingAddress.name  && <p className="font-semibold text-gray-800">{order.billingAddress.name}</p>}
                        {order.billingAddress.email && (
                          <p className="flex items-start gap-2 break-all">
                            <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600 break-all">{order.billingAddress.email}</span>
                          </p>
                        )}
                        {order.billingAddress.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            {order.billingAddress.phone}
                          </p>
                        )}
                        {order.billingAddress.street && <p>{order.billingAddress.street}</p>}
                        <p>{[order.billingAddress.city, order.billingAddress.state, order.billingAddress.zipCode].filter(Boolean).join(", ")}</p>
                      </div>
                    )}
                  </section>
                )}

                {/* Tracking */}
                {order.trackingNumber && (
                  <section className="bg-violet-50 rounded-2xl p-4 sm:p-5 border border-violet-100">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Truck className="w-4 h-4 text-violet-600" />Tracking
                    </h3>
                    <div className="font-mono text-sm text-violet-700 bg-white px-3 py-2.5 rounded-xl border border-violet-100 break-all">
                      {order.trackingNumber}
                    </div>
                    {order.vendor && (
                      <p className="text-xs text-gray-500 mt-2">
                        Via <span className="font-medium text-gray-700">{order.vendor}</span>
                        {order.pickupTime && ` · Pickup: ${new Date(order.pickupTime).toLocaleString("en-IN")}`}
                      </p>
                    )}
                  </section>
                )}

                {/* Bottom actions */}
                <div className="space-y-2.5 pb-2">
                  {canDownload && (
                    <button
                      onClick={() => { onDownloadInvoice?.(); onClose(); }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold text-sm transition-all duration-200"
                    >
                      <Download className="w-4 h-4" />Download Invoice
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl font-medium text-sm transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sub-Modals ── */}

      {activeSubModal === "cancel" && (
        <CancelReasonModal
          isCustom={isCustom}
          paymentMethod={order.payment_method || order.paymentMethod || "COD"}
          onConfirm={(reason, isRefunded) =>
            commitStatus("Cancelled", {
              cancelReason: reason,
              refundStatus: isRefunded ? "Refund Processing" : "No Refund",
              ...(isRefunded && { initiateRefund: true }),
            })
          }
          onClose={() => setActiveSubModal(null)}
        />
      )}

      {activeSubModal === "shipping" && (
        <ShippingDetailsModal
          initialData={{
            vendor:         localOrderData.vendor         || order.vendor         || "",
            trackingNumber: localOrderData.trackingNumber || order.trackingNumber || "",
            pickupTime:     localOrderData.pickupTime     || order.pickupTime     || "",
          }}
          onConfirm={(d) => commitStatus("Shipped", d)}
          onClose={() => setActiveSubModal(null)}
        />
      )}

      {activeSubModal === "flag" && (
        <DeliveryFlagModal
          currentFlags={currentFlags}
          onFlag={(n) =>
            commitStatus("Returned", {
              description:     "Customer unavailable at delivery time",
              flag_count:      n,
              account_blocked: n >= 4,
            })
          }
          onClose={() => setActiveSubModal(null)}
        />
      )}

      {activeSubModal === "returnPolicy" && (
        <ReturnPolicyModal
          isCustom={isCustom}
          onAccept={(reasonId, reasonLabel, note, imgPreviews, vidPreviews, imgBase64, vidBase64) => {
            setLocalOrderData((prev) => ({
              ...prev,
              returnImages: imgPreviews,
              returnVideos: vidPreviews,
            }));
            commitStatus("Return Requested", {
              returnInitiated: true,
              returnReasonId:  reasonId,     // e.g. "1" — sent to backend as return_reason
              returnReason:    reasonLabel,  // e.g. "Damaged product" — for timeline/UI display only
              ...(note              && { returnNote:   note        }),
              ...(imgBase64?.length && { returnImages: imgBase64   }),
              ...(vidBase64?.length && { returnVideos: vidBase64   }),
            });
          }}
          onClose={() => setActiveSubModal(null)}
        />
      )}

      {activeSubModal === "returnInspect" && (
        <ReturnInspectModal
          order={mergedOrder}
          isCustom={isCustom}
          onApprove={() => commitStatus("Return Approved", { returnInitiated: true })}
          onReject={(reason) => commitStatus("Return Rejected", { rejectionReason: reason })}
          onClose={() => setActiveSubModal(null)}
        />
      )}

      {activeSubModal === "inspection" && (
        <ReturnInspectionModal
          isCustom={isCustom}
          onDecision={(decision, selected) => {
            const isFakeReturn = decision === "reject_no_refund";
            commitStatus(selected.result, {
              inspectionResult: decision,
              refundStatus:
                decision === "full_refund"    ? "Refund Processed"         :
                decision === "partial_refund" ? "Partial Refund Processed" :
                decision === "replacement"    ? "Replacement Initiated"    :
                                                "No Refund",
              ...(isFakeReturn && {
                flag_count:      (currentFlags || 0) + 1,
                account_blocked: (currentFlags || 0) + 1 >= 4,
              }),
            });
          }}
          onClose={() => setActiveSubModal(null)}
        />
      )}
    </>
  );
};

export default OrderDetailsModal;